import {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { eventData } from "data/chartData/eventData";
import unifiedData from "data/chartData/unifiedData";
import { queueData } from "data/chartData/queueData";
import { toast } from "sonner";
import { format, addMinutes, parseISO } from "date-fns";
import { eventOptions } from "data/translationArrays";
import { useStateContext } from "contexts/ContextProvider";
import { loadProjectUsers } from "helpers/projectUsers";
import { createEvent, loadProjectEvents } from "helpers/projectEvents";

const EventContext = createContext();

const colorPalette = {
  event: "#0073b7",
  notification: "#ff851b",
  queue: "#00a65a",
};

const EventsProvider = ({ children }) => {
  const [events, setEvents] = useState([]); //
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("timeGridWeek");
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isEditingEvent, setIsEditingEvent] = useState(false);

  const [userData, setUserData] = useState([]);
  const { userProfile } = useStateContext();
  const currentProjectId = userProfile?.currentProjectId;
  //const { userData, setUserData } = useStateContext();

  const fetchData = async () => {
    if (!currentProjectId) {
      setUserData([]);
      return;
    }
    try {
      const members = await loadProjectUsers(currentProjectId);
      setUserData(
        members.map((member) => ({
          id: member.userId,
          username: member.name || member.email || "",
          fullname: member.name || member.email || "",
          role: member.role || "guest",
        }))
      );
    } catch (error) {
      console.error("Error fetching Project users:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentProjectId]);

  const fetchEventsFromDB = useCallback(async (type = "all", { throwOnError = false } = {}) => {
    if (!currentProjectId) return [];
    try {
      return await loadProjectEvents(currentProjectId, type);
    } catch (error) {
      console.error("Error fetching Project events:", error);
      if (throwOnError) throw error;
      return [];
    }
  }, [currentProjectId]);

  const normalizeEvents = useCallback((dbEvents) => dbEvents.map((event) => {
    const start = new Date(event.start);
    const end = event.end
      ? new Date(event.end)
      : addMinutes(start, event.durationMinutes ?? event.duration ?? 0);
    const user = userData.find((candidate) => String(candidate.id) === String(event.user_id));
    return {
      ...event,
      title: event.title || user?.fullname,
      user_id: event.user_id,
      userName: user?.username || "",
      userRole: user?.role || "",
      start,
      end,
      duration: event.duration ?? event.durationMinutes ?? null,
      urgency: event.urgency ?? null,
      assigned: event.assigned ?? null,
      comments: event.comments ?? null,
      type: event.type || "event",
      backgroundColor: colorPalette[event.type] || colorPalette.event,
      parcel_order: event.parcel_order ?? event.parcelOrder ?? null,
    };
  }), [userData]);

  const refreshEvents = useCallback(async ({ throwOnError = false } = {}) => {
    try {
      const dbEvents = await fetchEventsFromDB("all", { throwOnError: true });
      const normalized = normalizeEvents(dbEvents);
      setEvents(normalized);
      return normalized;
    } catch (error) {
      if (throwOnError) throw error;
      return [];
    }
  }, [fetchEventsFromDB, normalizeEvents]);

  useEffect(() => {
    const initializeEvents = async () => {
      const storedEvents = localStorage.getItem("events");

      if (storedEvents) {
        try {
          setEvents(JSON.parse(storedEvents));
        } catch {
          localStorage.removeItem("events");
        }
      }

      // The cache is only an immediate fallback. Persisted Events are the
      // authoritative source and must replace it without a logout/login.
      await refreshEvents();
      return;
      /* Legacy normalization path retained below only as a historical comment.
      const dbEvents = await fetchEventsFromDB();

        const mergedEvents = dbEvents.map((event) => {
          // Keep the backend UTC instant intact. Presentation components format
          // it in Europe/Madrid; converting it into a different Date here would
          // shift the actual selected slot on a later save.
          const start = new Date(event.start);
          let end = new Date(event.end);

          if (!event.end && event.duration) {
            end = addMinutes(start, event.duration);
          }

          return {
            ...event,

            title:
              event.title ||
              userData.find((user) => user.id === event.user_id)?.fullname,
            user_id: event.user_id,
            userName:
              userData.find((user) => user.id === event.user_id)?.username ||
              "",
            userRole:
              userData.find((user) => user.id === event.user_id)?.role || "",
            start: start,
            duration: event.duration || null,
            end: end,
            urgency: event.urgency || null,
            assigned: event.assigned || null, // Incluir lógica para mostrar nombre de usuario asignado
            comments: event.comments || null,
            type: event.type || "event",
            backgroundColor: colorPalette[event.type] || colorPalette.event,
            parcel_order: event.parcel_order || null,
          };
        });

        setEvents(mergedEvents);

        localStorage.setItem("events", JSON.stringify(mergedEvents));

      /* } else {
        const mergedEvents = [];

        eventData.forEach(event => {
          const start = event.start instanceof Date ? event.start : new Date(event.start);
          let end = event.end instanceof Date ? event.end : new Date(event.end);
          if (!event.end && event.duration) {
            end = new Date(start.getTime() + event.duration * 60000); // Convert duration from minutes to milliseconds
          }

          mergedEvents.push({
            ...event,
            //id: event.event_id,
            title: event.title || userData.find(user => user.id === event.user_id)?.fullname,
            user_id: event.user_id,
            userName: userData.find(user => user.id === event.user_id)?.username || "",
            userRole: userData.find(user => user.id === event.user_id)?.role || "",
            start: start,
            duration: event.duration || null,
            end: end,
            urgency: event.urgency || null,
            assigned: event.assigned || null,
            comments: event.comments || null,
            type: event.type || 'event',
            backgroundColor: colorPalette[event.type] || colorPalette.event,
          });
        });

        setEvents(mergedEvents);
        console.log("eventHandlers events", mergedEvents);
        localStorage.setItem('events', JSON.stringify(mergedEvents));
      } */
    };

    if (userData.length > 0) {
      initializeEvents();
    }
  }, [refreshEvents]);

  useEffect(() => {
    if (!currentProjectId) return undefined;
    const refreshWhenActive = () => {
      if (!document.hidden) refreshEvents();
    };
    const interval = window.setInterval(refreshWhenActive, 8000);
    window.addEventListener("focus", refreshWhenActive);
    document.addEventListener("visibilitychange", refreshWhenActive);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshWhenActive);
      document.removeEventListener("visibilitychange", refreshWhenActive);
    };
  }, [currentProjectId, refreshEvents]);

  useEffect(() => {
    /* console.log("eventHandlers events: ", events) */
    if (events.length > 0) {
      localStorage.setItem("events", JSON.stringify(events));
    } else {
      localStorage.removeItem("events");
    }
  }, [events]);

  const handleEventClick = useCallback((clickInfo) => {
    setCurrentEvent(clickInfo.event);
    setIsPopoverOpen(true);
  }, []);

  const handleUpdateEvent = (updatedEvent) => {
    // TODO ONLY ACCEPT EVENT_ID or ID
    const updatedEvents = events.map((localEvent) =>
      localEvent.event_id === updatedEvent.event_id ||
      localEvent.event_id === updatedEvent.id
        ? { ...localEvent, ...updatedEvent }
        : localEvent
    );

    setEvents(updatedEvents);
  };

  const handleAddTask = async (newTask) => {
    /* console.log("EventHandlers handleAddTask newTask: ", newTask); */
    setIsEditingEvent(true);
    if (!currentProjectId) {
      setIsEditingEvent(false);
      return;
    }
    const parsedUserId = JSON.parse(sessionStorage.getItem("userId"));

    //Extract and format the variables
    const userId = parsedUserId;
    const title = newTask.nameTask;
    const start = new Date(newTask.start).toISOString();
    const end = new Date(newTask.end).toISOString();
    //const duration = (new Date(newTask.end) - new Date(newTask.start)) / 1000; // Duration in seconds
    const urgency = newTask.priority;
    const assigned = newTask.assignedUser || ""; // Handle empty assignedUser
    const comments = newTask.comments || ""; // Assuming comments might be in newTask
    const type = newTask.type;
    const parcela = newTask.parcela || null;

    const jsonPayload = {
      userId,
      title,
      start,
      end,
      urgency,
      assigned,
      comments,
      type,
      parcela,
      queryType: "normal",
    };

    try {
      const response = await createEvent(currentProjectId, jsonPayload);

      /* console.log("EventHandlers handleAddTask createEvent response: ", response); */
      toast.success(response?.message || "Evento creado satisfactoriamente");

      const updatedEvents = await fetchEventsFromDB();
      setEvents(updatedEvents);
      setIsEditingEvent(false);
    } catch (error) {
      console.error("Error adding task: ", error);
      toast.error(error.message || "Error adding task");
    }
  }; //End handleAddTask

  const [fetchedData, setFetchedData] = useState([]);

  const transformData = (events) => {
    return events.map((event) => ({
      id: event.event_id,
      message: event.title,
      location: event.parcela || event.location,
      updated: event.start,
      urgency: event.urgency ? parseInt(event.urgency, 10) : null,
      assigned: event.assigned,
      type:
        eventOptions.find((option) => option.value === event.type)?.label ||
        event.type,
      backgroundColor: colorPalette[event.type] || colorPalette.event,
    }));
  };

  useEffect(() => {
    const fetchAndSetData = async () => {
      const eventsFromDB = await fetchEventsFromDB();

      const transformedData = transformData(eventsFromDB);

      setFetchedData(transformedData);
    };

    fetchAndSetData();
  }, [setIsEditingEvent]);

  return (
    <EventContext.Provider
      value={{
        events,
        setEvents,
        isDropdownOpen,
        setIsDropdownOpen,
        showEditModal,
        setShowEditModal,
        currentEvent,
        setCurrentEvent,
        currentView,
        setCurrentView,
        currentDate,
        setCurrentDate,
        handleEventClick,
        handleUpdateEvent,
        isPopoverOpen,
        setIsPopoverOpen,
        handleAddTask,
        fetchEventsFromDB,
        refreshEvents,
        isEditingEvent,
        setIsEditingEvent,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};
export { EventsProvider };

export const useEventHandlers = () => useContext(EventContext);
